import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useWalletAuth } from '../context/WalletAuthContext';
import NotificationBell from '../components/NotificationBell';
import { superAdminAPI } from '../lib/walletApi';
import {
  LayoutDashboard, Building2, Store, Scale, BarChart3,
  ShieldCheck, Users, ScrollText, LogOut, Menu, X, Search, Banknote,
  Landmark, Brain, Wallet, CreditCard, FileCheck, AlertOctagon,
  Settings, Activity, ArrowDownCircle, GraduationCap, Send, TrendingUp
} from 'lucide-react';

const NAV = [
  { path: '/super-admin', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { path: '/super-admin/companies', icon: Building2, label: 'Companies' },
  { path: '/super-admin/banking-partners', icon: Landmark, label: 'Banking Partners' },
  { path: '/super-admin/virtual-accounts', icon: CreditCard, label: 'Virtual Accounts' },
  { path: '/super-admin/approvals', icon: FileCheck, label: 'Approvals' },
  { path: '/super-admin/transaction-errors', icon: AlertOctagon, label: 'Error Monitor' },
  { path: '/super-admin/credit-wallet', icon: Wallet, label: 'Credit Wallet' },
  { path: '/super-admin/manual-inflow', icon: ArrowDownCircle, label: 'Manual Inflow' },
  { path: '/super-admin/student-accounts', icon: GraduationCap, label: 'Employee Accounts' },
  { path: '/super-admin/transfers', icon: Send, label: 'Transfer to Bank' },
  { path: '/super-admin/transactions', icon: ScrollText, label: 'Transactions' },
  { path: '/super-admin/daily-payouts', icon: TrendingUp, label: 'Daily Payouts' },
  { path: '/super-admin/analytics', icon: Brain, label: 'User Intelligence' },
  { path: '/super-admin/wallet-payroll', icon: Banknote, label: 'Wallet & Payroll' },
  { path: '/super-admin/vendors', icon: Store, label: 'Global Vendors' },
  { path: '/super-admin/reconciliation', icon: Scale, label: 'Reconciliation' },
  { path: '/super-admin/reports', icon: BarChart3, label: 'Reports' },
  { divider: true, label: 'Administration' },
  { path: '/super-admin/admins', icon: ShieldCheck, label: 'Admins' },
  { path: '/super-admin/users', icon: Users, label: 'All Users' },
  { path: '/super-admin/audit-logs', icon: ScrollText, label: 'Audit Logs' },
  { divider: true, label: 'System' },
  { path: '/super-admin/settings', icon: Settings, label: 'Settings', badge: 'settings' },
  { path: '/super-admin/system-status', icon: Activity, label: 'System Status', badge: 'status' },
];

export default function SuperAdminLayout() {
  const { user, logout } = useWalletAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [configStatus, setConfigStatus] = useState(null);
  const handleLogout = () => { logout(); };

  useEffect(() => {
    const loadStatus = async () => {
      try {
        const cs = await superAdminAPI.getConfigStatus();
        setConfigStatus(cs);
      } catch {}
    };
    loadStatus();
    const interval = setInterval(loadStatus, 60000);
    return () => clearInterval(interval);
  }, []);

  // Determine badge states
  const hasMisconfiguration = configStatus && (
    !configStatus.proxy_active ||
    !configStatus.ip_matches_fixie ||
    configStatus.url_source !== 'DATABASE'
  );
  const allHealthy = configStatus && configStatus.proxy_active && configStatus.ip_matches_fixie;

  const getBadge = (badgeType) => {
    if (badgeType === 'settings' && hasMisconfiguration) {
      return <span className="w-2 h-2 rounded-full bg-red-500 absolute -top-0.5 -right-0.5" data-testid="settings-badge-red" />;
    }
    if (badgeType === 'status') {
      return (
        <span className={`w-2 h-2 rounded-full absolute -top-0.5 -right-0.5 ${allHealthy ? 'bg-emerald-400' : 'bg-red-500'}`}
          data-testid={allHealthy ? 'status-badge-green' : 'status-badge-red'} />
      );
    }
    return null;
  };

  // Truncate URL for status bar
  const shortUrl = configStatus?.active_url
    ? configStatus.active_url.replace('https://', '').replace('http://', '')
    : '...';

  const Sidebar = () => (
    <div className="flex flex-col h-full">
      <div className="p-5 border-b border-gray-100">
        <img src="/pebbles_logo.svg" alt="Pebbles" className="h-8" />
      </div>
      <nav className="flex-1 py-4 px-3 space-y-0.5 overflow-y-auto">
        {NAV.map((item, i) => item.divider ? (
          <p key={i} className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-3 pt-5 pb-2">{item.label}</p>
        ) : (
          <NavLink key={item.path} to={item.path} end={item.end}
            onClick={() => setSidebarOpen(false)}
            title={item.badge === 'settings' ? 'System Settings' : undefined}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                isActive ? 'bg-pink-50 text-pink-600 font-medium' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`
            }>
            <span className="relative">
              <item.icon className="w-[18px] h-[18px]" />
              {item.badge && getBadge(item.badge)}
            </span>
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-gray-100">
        <button onClick={handleLogout} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-500 hover:bg-red-50 hover:text-red-600 w-full transition-colors" data-testid="sa-logout-btn">
          <LogOut className="w-[18px] h-[18px]" /> Sign Out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50" data-testid="super-admin-layout">
      {/* Mobile overlay */}
      {sidebarOpen && <div className="fixed inset-0 bg-black/20 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}
      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 bottom-0 w-60 bg-white border-r border-gray-100 z-50 transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <Sidebar />
      </aside>
      {/* Main */}
      <div className="lg:ml-60">
        {/* Persistent Status Bar */}
        {configStatus && (
          <div className="bg-gray-900 text-white text-[11px] h-7 flex items-center px-4 lg:px-6 gap-4 overflow-x-auto cursor-pointer"
            onClick={() => navigate('/super-admin/settings')}
            data-testid="persistent-status-bar">
            <div className="flex items-center gap-1.5 shrink-0">
              <span className={`inline-block w-1.5 h-1.5 rounded-full ${configStatus.environment === 'PRODUCTION' ? 'bg-emerald-400' : 'bg-amber-400'}`} />
              <span className={`font-semibold ${configStatus.environment === 'PRODUCTION' ? 'text-emerald-400' : 'text-amber-400'}`}
                data-testid="statusbar-env">{configStatus.environment}</span>
            </div>
            <div className="w-px h-3 bg-gray-700 shrink-0" />
            <div className="flex items-center gap-1.5 shrink-0">
              <span className={`inline-block w-1.5 h-1.5 rounded-full ${configStatus.proxy_active ? 'bg-emerald-400' : 'bg-red-400'}`} />
              <span className="text-gray-400">Proxy</span>
            </div>
            <div className="w-px h-3 bg-gray-700 shrink-0" />
            <span className="text-gray-400 font-mono truncate" data-testid="statusbar-url">{shortUrl}</span>
          </div>
        )}
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-gray-100">
          <div className="flex items-center justify-between h-14 px-4 lg:px-6">
            <div className="flex items-center gap-3">
              <button className="lg:hidden p-2 rounded-lg hover:bg-gray-50" onClick={() => setSidebarOpen(true)}>
                <Menu className="w-5 h-5 text-gray-600" />
              </button>
              <div className="hidden sm:flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 text-sm text-gray-400 w-64">
                <Search className="w-4 h-4" /> Search...
              </div>
            </div>
            <div className="flex items-center gap-3">
              <NotificationBell />
              <div className="flex items-center gap-2 pl-3 border-l border-gray-100">
                <div className="w-8 h-8 rounded-full bg-pink-100 text-pink-600 flex items-center justify-center text-xs font-bold">
                  {user?.first_name?.[0]}{user?.last_name?.[0]}
                </div>
                <span className="text-sm font-medium text-gray-700 hidden sm:block">{user?.first_name}</span>
              </div>
            </div>
          </div>
        </header>
        <main className="p-4 lg:p-6"><Outlet /></main>
      </div>
    </div>
  );
}
